import aygLogo from "@assets/images/brand/rrc-icon.svg";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrophy,
  IconVolume,
  IconVolumeOff,
  IconX,
} from "@tabler/icons-react";
import {memo, useCallback, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import useScrollLock from "@/hooks/useScrollLock";
import {formatAmountSuffix} from "@/util/balance.util";
import {formatCurrency} from "@/util/formatCurrency";
import {useLogo} from "@/util/logoUtils";
import useStoryViewer from "../hooks/useStoryViewer";
import styles from "./StoryViewer.module.css";

/**
 * Side story preview — blurred, dimmed adjacent story card (IG desktop style).
 */
const SidePreview = memo(({story, onClick}) => {
  if (!story) return <div className={styles.sidePreviewPlaceholder} />;

  return (
    <div
      className={styles.sidePreview}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onClick?.()}
      aria-label={`Ver historia: ${story.name}`}
    >
      <img
        className={styles.sidePreviewImg}
        src={story.src}
        alt=""
        draggable={false}
        loading="lazy"
      />
      <div className={styles.sidePreviewOverlay}>
        <span className={styles.sidePreviewName}>{story.name}</span>
      </div>
    </div>
  );
});
SidePreview.displayName = "SidePreview";

/**
 * Instagram-like fullscreen story viewer.
 *
 * Desktop: dark overlay, 380×680 centered 9:16 container, blurred/dimmed side previews.
 * Mobile: fullscreen, no previews.
 *
 * Features: progress bars, auto-advance (5s images / video duration),
 * pause on mousedown/touch hold, keyboard nav, click-outside-close,
 * preload next image, pause inactive videos.
 */
const AYGStoryViewer = ({
  stories,
  initialIndex,
  onClose,
  onComplete,
  onGroupPrev,
  onViewed,
  avatar,
  displayName: headerName,
  profilePath,
}) => {
  const brandLogo = useLogo();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const overlayRef = useRef(null);
  const videoRef = useRef(null);
  const prevIndexRef = useRef(initialIndex);
  const [direction, setDirection] = useState(null);
  const [isMuted, setIsMuted] = useState(true);

  const {currentIndex, progress, isPaused, story, totalStories, goNext, goPrev, pause, resume} = useStoryViewer({
    stories,
    initialIndex,
    onClose,
    onComplete,
    onViewed,
  });

  // ── Swipe gesture detection for group navigation ──
  const touchStartRef = useRef({x: 0, y: 0, time: 0});
  const SWIPE_THRESHOLD = 60;
  const SWIPE_MAX_TIME = 400;

  const handleTouchStart = useCallback(e => {
    const touch = e.touches[0];
    touchStartRef.current = {x: touch.clientX, y: touch.clientY, time: Date.now()};
  }, []);

  const handleTouchEnd = useCallback(
    e => {
      const touch = e.changedTouches[0];
      const {x: startX, y: startY, time: startTime} = touchStartRef.current;
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      const elapsed = Date.now() - startTime;

      if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY) || elapsed > SWIPE_MAX_TIME) {
        return;
      }

      if (deltaX < 0) {
        if (currentIndex >= totalStories - 1) {
          onComplete?.();
        } else {
          goNext();
        }
      } else {
        if (currentIndex === 0 && onGroupPrev) {
          onGroupPrev();
        } else {
          goPrev();
        }
      }
    },
    [currentIndex, totalStories, onComplete, onGroupPrev, goNext, goPrev]
  );

  const prevStory = currentIndex > 0 ? stories[currentIndex - 1] : null;
  const nextStory = currentIndex < totalStories - 1 ? stories[currentIndex + 1] : null;

  // Direction tracking for slide animation
  useEffect(() => {
    if (currentIndex > prevIndexRef.current) setDirection("next");
    else if (currentIndex < prevIndexRef.current) setDirection("prev");
    prevIndexRef.current = currentIndex;
  }, [currentIndex]);

  // Focus trap — grab focus on mount, restore on unmount
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const prev = document.activeElement;
    el.focus();
    return () => prev?.focus?.();
  }, []);

  useScrollLock(true);

  // Preload next story image
  useEffect(() => {
    if (nextStory && nextStory.type !== "video" && nextStory.src) {
      const img = new Image();
      img.src = nextStory.src;
    }
  }, [nextStory]);

  // Pause/play video when paused state changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPaused) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  }, [isPaused]);

  // Click outside story container to close
  const handleOverlayClick = useCallback(
    e => {
      if (e.target === overlayRef.current) onClose();
    },
    [onClose]
  );

  // Pause on long-press only (>150ms), short tap = navigate (IG behavior)
  const holdTimerRef = useRef(null);
  const handlePointerDown = useCallback(() => {
    holdTimerRef.current = setTimeout(() => {
      pause();
      holdTimerRef.current = null;
    }, 150);
  }, [pause]);
  const handlePointerUp = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    } else {
      resume();
    }
  }, [resume]);

  const slideClass = direction === "next" ? styles.slideEnter : direction === "prev" ? styles.slideEnterPrev : "";

  return createPortal(
    <div
      ref={overlayRef}
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Story viewer"
      onClick={handleOverlayClick}
      tabIndex={-1}
    >
      {/* Branding — top-left */}
      <div className={styles.branding}>
        <img
          className={styles.brandingLogo}
          src={brandLogo}
          alt="Randrush"
          draggable={false}
        />
      </div>

      {/* Close — top-right */}
      <button
        type="button"
        className={styles.closeBtn}
        onClick={onClose}
        aria-label="Cerrar historia"
      >
        <IconX
          size={32}
          stroke={2.5}
        />
      </button>

      {/* Stories track: prevPreview | ◀ | main story | ▶ | nextPreview */}
      <div className={styles.storiesTrack}>
        <SidePreview
          story={prevStory}
          onClick={goPrev}
        />

        <button
          type="button"
          className={`${styles.arrowBtn} ${!prevStory ? styles.arrowBtnDisabled : ""}`}
          onClick={goPrev}
          aria-label="Historia anterior"
        >
          <IconChevronLeft size={16} />
        </button>

        {/* Main Story Container — 380×680 */}
        <div
          className={styles.storyContainer}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Progress bars */}
          <div className={styles.progressRow}>
            {stories.map((s, i) => (
              <div
                key={s.id}
                className={styles.progressSegment}
              >
                <div
                  className={styles.progressFill}
                  style={{
                    width: i < currentIndex ? "100%" : i === currentIndex ? `${progress}%` : "0%",
                  }}
                />
              </div>
            ))}
          </div>

          {/* Header: avatar | name | time | spacer | actions */}
          <div className={styles.storyHeader}>
            <button
              type="button"
              className={styles.headerAvatarBtn}
              onClick={() => {
                onClose();
                navigate(profilePath || "/provider/ayg");
              }}
              aria-label="Ir al perfil"
            >
              <div className={styles.headerRing}>
                <div className={styles.headerRingInner}>
                  <img
                    className={styles.headerRingImg}
                    src={avatar || aygLogo}
                    alt={story.name || "Randrush"}
                  />
                </div>
              </div>
            </button>
            <span
              className={`${styles.headerName} ${story.type === "bigwin" || story.type === "topgame" ? styles.headerNameBrand : ""}`}
            >
              {headerName || story.name}
            </span>
            <span className={styles.headerTime}>8 h</span>
            <span className={styles.headerSpacer} />
            <div className={styles.headerActions}>
              {(story.type === "video" || (story.type === "bigwin" && story.mediaSrc)) && (
                <button
                  type="button"
                  className={styles.headerActionBtn}
                  onClick={() => setIsMuted(m => !m)}
                  aria-label={isMuted ? "Activar sonido" : "Silenciar"}
                >
                  {isMuted ? <IconVolumeOff size={18} /> : <IconVolume size={18} />}
                </button>
              )}
              {isPaused && (
                <button
                  type="button"
                  className={styles.headerActionBtn}
                  aria-label="En pausa"
                >
                  <IconPlayerPause size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div
            className={styles.content}
            key={currentIndex}
          >
            {story.type === "video" ? (
              <>
                <video
                  ref={videoRef}
                  className={`${styles.storyVideo} ${slideClass}`}
                  src={story.src}
                  autoPlay
                  muted={isMuted}
                  playsInline
                  preload="auto"
                />
                {story.gameId && (
                  <button
                    type="button"
                    className={styles.gameCard}
                    onClick={() => {
                      onClose();
                      navigate(`/games/${story.gameProvider}/${story.gameId}`);
                    }}
                  >
                    <img
                      className={styles.gameCardImg}
                      src={story.src}
                      alt={story.winnerGame}
                      draggable={false}
                    />
                    <span className={styles.gameCardName}>{story.winnerGame}</span>
                  </button>
                )}
              </>
            ) : story.type === "bigwin" ? (
              <div className={`${styles.bigwinSlide} ${slideClass}`}>
                {story.mediaSrc ? (
                  <video
                    ref={videoRef}
                    className={styles.bigwinMedia}
                    src={story.mediaSrc}
                    autoPlay
                    muted={isMuted}
                    playsInline
                    preload="auto"
                  />
                ) : (
                  <>
                    <img
                      className={styles.bigwinBgImage}
                      src={story.src}
                      alt=""
                      draggable={false}
                    />
                    <div className={styles.bigwinBgScrim} />
                    <button
                      type="button"
                      className={styles.bigwinThumb}
                      onClick={() => {
                        if (story.gameId) {
                          onClose();
                          navigate(`/games/${story.gameProvider}/${story.gameId}`);
                        }
                      }}
                    >
                      <img
                        className={styles.bigwinThumbImg}
                        src={story.src}
                        alt={story.winnerGame}
                        draggable={false}
                      />
                    </button>
                  </>
                )}
                <div className={styles.bigwinOverlay}>
                  <div className={styles.bigwinBadge}>
                    <IconTrophy size={16} />
                    <span>BIG WIN!</span>
                  </div>
                  <span className={styles.bigwinUser}>@{story.winnerAlias}</span>
                  <span className={styles.bigwinAmount}>
                    +{formatAmountSuffix(story.winnerAmount)} {formatCurrency(story.winnerCurrency)}
                  </span>
                  <span className={styles.bigwinGame}>{story.winnerGame}</span>
                </div>
              </div>
            ) : story.type === "topgame" ? (
              <div className={`${styles.bigwinSlide} ${slideClass}`}>
                <img
                  className={styles.bigwinBgImage}
                  src={story.src}
                  alt=""
                  draggable={false}
                />
                <div className={styles.bigwinBgScrim} />
                <span className={styles.topgameHeader}>{story.providerTitle}</span>
                <button
                  type="button"
                  className={styles.bigwinThumb}
                  onClick={() => {
                    if (story.gameId) {
                      onClose();
                      navigate(`/games/${story.gameProvider}/${story.gameId}`);
                    }
                  }}
                >
                  <img
                    className={styles.bigwinThumbImg}
                    src={story.src}
                    alt={story.gameName}
                    draggable={false}
                  />
                </button>
                <div className={styles.bigwinOverlay}>
                  <span className={styles.topgameRank}>#{story.rank}</span>
                  <span className={styles.topgameTitle}>{story.gameName}</span>
                  {story.gameId && (
                    <button
                      type="button"
                      className={styles.topgamePlayBtn}
                      onClick={() => {
                        onClose();
                        navigate(`/games/${story.gameProvider}/${story.gameId}`);
                      }}
                    >
                      <IconPlayerPlay size={16} />
                      {t("playNow")}
                    </button>
                  )}
                </div>
              </div>
            ) : story.type === "placeholder" ? (
              <div className={`${styles.storyPlaceholder} ${slideClass}`}>
                <span className={styles.storyPlaceholderText}>{story.name}</span>
              </div>
            ) : (
              <img
                className={`${styles.storyImage} ${slideClass}`}
                src={story.src}
                alt={story.name}
                draggable={false}
              />
            )}
          </div>

          {/* Nav zones (tap/click inside story) */}
          <div className={styles.navZones}>
            <button
              type="button"
              className={styles.navPrev}
              onClick={goPrev}
              aria-label="Historia anterior"
            />
            <button
              type="button"
              className={styles.navNext}
              onClick={goNext}
              aria-label="Siguiente historia"
            />
          </div>

          {/* Pause indicator */}
          {isPaused && <span className={styles.pauseIndicator}>Paused</span>}
        </div>

        <button
          type="button"
          className={`${styles.arrowBtn} ${!nextStory ? styles.arrowBtnDisabled : ""}`}
          onClick={goNext}
          aria-label="Siguiente historia"
        >
          <IconChevronRight size={16} />
        </button>

        <SidePreview
          story={nextStory}
          onClick={goNext}
        />
      </div>
    </div>,
    document.body
  );
};

export default memo(AYGStoryViewer);
